import { Flex } from "antd";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { styled } from "styled-components";

const DEFAULT_AUTO_SAVE_ID = "storyboard-menu-panel";

const MENU_MIN_PIXELS = 450;
const MENU_MAX_PERCENT = 50;
// Cap the px-derived minimum strictly below the max so the menu can never be
// "locked" at 50% (min === max). On a narrow viewport that lock would clamp the
// layout to 50/50 and autoSave would persist that bad value.
const MENU_MIN_CAP_PERCENT = 40;
const MENU_DEFAULT_PERCENT = 20;

export const ResizableScenesLayout = ({
  menu,
  children,
  autoSaveId = DEFAULT_AUTO_SAVE_ID,
}: {
  menu: ReactNode;
  children: ReactNode;
  autoSaveId?: string;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [groupWidth, setGroupWidth] = useState(0);

  // Measure synchronously in a layout effect so the first PanelGroup mount
  // already has the correct defaultSize/minSize. useResizeObserver defers via
  // requestAnimationFrame, which is too late and causes a layout pop.
  useLayoutEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;
    setGroupWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (typeof width === "number") setGroupWidth(width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Render the PanelGroup on the first render (no early return) so its
  // autoSaveId layout is restored on mount. Deferring the mount until the width
  // was measured caused the persisted size to be dropped on reload. Until the
  // width is known we fall back to the default percentage for the pixel-derived
  // minimum; the layout effect above re-measures and corrects it before paint.
  const menuMinSize =
    groupWidth > 0
      ? Math.min(MENU_MIN_CAP_PERCENT, (MENU_MIN_PIXELS / groupWidth) * 100)
      : MENU_DEFAULT_PERCENT;

  return (
    <StyledPanelGroupWrapper ref={wrapperRef}>
      <PanelGroup
        direction="horizontal"
        autoSaveId={autoSaveId}
        style={{ width: "100%", height: "100%" }}
      >
        <Panel
          id="scenes-canvas"
          order={1}
          style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
        >
          {children}
        </Panel>
        <PanelResizeHandle>
          <StyledResizeHandleBar />
        </PanelResizeHandle>
        <Panel
          id="scenes-settings"
          order={2}
          defaultSize={Math.max(MENU_DEFAULT_PERCENT, menuMinSize)}
          minSize={menuMinSize}
          maxSize={MENU_MAX_PERCENT}
        >
          {menu}
        </Panel>
      </PanelGroup>
    </StyledPanelGroupWrapper>
  );
};

const StyledPanelGroupWrapper = styled.div`
  display: flex;
  flex: 1;

  /* Keep the scenes side within its flex track so a zoomed-wide scene scrolls
     inside the panel instead of expanding it over the thumbnail rail. */
  min-width: 0;
  width: 100%;
  height: 100%;

  div[data-resize-handle-active="keyboard"],
  div[data-resize-handle-active="pointer"] {
    background: ${({ theme }) => theme.colorPrimary};
    > div {
      background: none;
    }
  }
`;

const StyledResizeHandleBar = styled.div`
  width: 1px;
  height: 100%;
  background: ${({ theme }) => theme.colorBorder};
`;

// A full-page snap target sized to the scenes viewport. The slide is centered
// so it takes the whole height when width allows, or stays centered when width
// is the constraint. Used by the slide-based (Flash) editor only.
export const SlideSnapPage = styled.div<{ $minHeight: number }>`
  display: flex;
  align-items: center;

  /* "safe" keeps the scene start-aligned (reachable) instead of clipped on both
     sides when it is zoomed wider than the viewport. */
  justify-content: safe center;
  min-height: ${({ $minHeight }) => $minHeight}px;
  padding-inline: 24px 40px;
  scroll-snap-align: start;
`;

export const TemplateArea = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledScrollableFlex = styled(Flex)<{
  $snap?: boolean;
  $snapPaddingTop?: number;
}>`
  width: 100%;

  /* min-width:0 lets this flex child shrink to its track so horizontal overflow
     scrolls here instead of widening the panel. */
  min-width: 0;

  /* Vertical scroll; horizontal pans when a scene is zoomed wider than the viewport. */
  overflow: auto scroll;
  position: relative;
  flex: 1;
  padding-block: 24px;
  scroll-snap-type: ${({ $snap }) => ($snap ? "y mandatory" : "none")};
  scroll-padding-block-start: ${({ $snap, $snapPaddingTop }) =>
    $snap ? `${$snapPaddingTop ?? 0}px` : "0"};

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colorBorder} transparent;

  /* Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colorBorder};
    border-radius: 3px;
    opacity: 0.5;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colorTextDescription};
    opacity: 0.7;
  }
`;
